import React from 'react';
import uuid4 from 'uuid/v4';

import {
  calculateScrollDimensions,
  calculateScrollOffsetWhenDraggingScrollbar
} from './utils';
import './styles.css';

/*
  How to use:
  
  Exporting your component:
  
  ```
    export default withScrollbar(MyComponent);
  ```

  In your component there would be a method in props that you need to call in order to set the container element:
  
  ```
    componentDidMount() {
      this.props.setScrollableContainerRef(
        document.getElementById('myContainer')
      );
    }
  ```

  You can also use your containers's ref and pass in `this.mycontainerRef.current` instead of `document.getElement...`:
  
  ```
    componentDidMount() {
      this.props.setScrollableContainerRef(this.containerRef.current);
    }
  ```

  In your component there would be a `Scrollbar` key in the `props` that you would need to call in the scrollable container:
  
  ```
    {HorizontalScrollbar}
    {VerticalScrollbar}
  ```
*/

const defaultOptions = {horizontal: true, vertical: true};

const withScrollbar = (WrappedComponent, customOptions = {}) => {
  const options = {...defaultOptions, ...customOptions};

  class Wrapper extends React.Component<any> {
    componentRef: any = null;
    horizontalScrollbarRef: any = React.createRef();
    verticalScrollbarRef: any = React.createRef();
    horizontalScrollUuid = uuid4();
    verticalScrollUuid = uuid4();

    state = {
      horizontalScrollbarMouseDown: false,
      verticalScrollbarMouseDown: false,
      startX: 0,
      startY: 0,
      currentScrollLeft: 0,
      currentScrollTop: 0
    };

    componentDidMount() {
      window.addEventListener('mouseup', this.scrollBarDragStop);
      window.addEventListener('mousemove', this.onDocumentMouseMove);
    }

    componentWillUnmount() {
      window.removeEventListener('mouseup', this.scrollBarDragStop);
      window.removeEventListener('mousemove', this.onDocumentMouseMove);
      this.componentRef.removeEventListener('scroll', this.onScroll);
    }

    // When the container is scrolled
    onScroll = () => {
      this.onHorizontalScroll();
      this.onVerticalScroll();
    };

    // When we drag the scrollbar with the mouse
    onDocumentMouseMove = event => {
      this.onHorizontalScrollbarDrag(event);
      this.onVerticalScrollbarDrag(event);
    };

    horizontalScrollBarDragStart = event => {
      this.setState({
        horizontalScrollbarMouseDown: true,
        startX: event.pageX,
        currentScrollLeft: this.componentRef.scrollLeft
      });
    };

    verticalScrollBarDragStart = event => {
      this.setState({
        verticalScrollbarMouseDown: true,
        startY: event.pageY,
        currentScrollTop: this.componentRef.scrollTop
      });
    };

    scrollBarDragStop = () => {
      this.setState({
        horizontalScrollbarMouseDown: false,
        verticalScrollbarMouseDown: false
      });
    };

    onHorizontalScrollbarDrag = event => {
      if (this.state.horizontalScrollbarMouseDown) {
        const {startX, currentScrollLeft} = this.state;
        const {scrollWidth, offsetWidth} = this.componentRef;

        const horizontalOffset = calculateScrollOffsetWhenDraggingScrollbar(
          scrollWidth,
          offsetWidth,
          startX,
          event.pageX
        );

        this.componentRef.scrollLeft = currentScrollLeft + horizontalOffset;
      }
    };

    onVerticalScrollbarDrag = event => {
      if (this.state.verticalScrollbarMouseDown) {
        const {offsetHeight, scrollHeight} = this.componentRef;
        const {startY, currentScrollTop} = this.state;

        const verticalOffset = calculateScrollOffsetWhenDraggingScrollbar(
          scrollHeight,
          offsetHeight,
          startY,
          event.pageY
        );

        this.componentRef.scrollTop = currentScrollTop + verticalOffset;
      }
    };

    setupScrollInfo = componentRef => {
      this.componentRef = componentRef;

      // Remove any previous event listeners that we might have attached
      this.componentRef.removeEventListener('scroll', this.onScroll);
      this.componentRef.addEventListener('scroll', this.onScroll);

      this.onScroll();
    };

    onHorizontalScroll = () => {
      if (options.horizontal && this.horizontalScrollbarRef.current) {
        const {scrollWidth, offsetWidth, scrollLeft} = this.componentRef;
        const {
          offset: scrollbarLeft,
          length: scrollbarWidth
        } = calculateScrollDimensions(scrollWidth, offsetWidth, scrollLeft);

        this.horizontalScrollbarRef.current.style.left = `${scrollbarLeft}px`;
        this.horizontalScrollbarRef.current.style.width = `${scrollbarWidth}px`;
      }
    };

    onVerticalScroll = () => {
      if (options.vertical && this.verticalScrollbarRef.current) {
        const {scrollHeight, offsetHeight, scrollTop} = this.componentRef;

        const {
          offset: scrollbarTop,
          length: scrollbarHeight
        } = calculateScrollDimensions(scrollHeight, offsetHeight, scrollTop);

        this.verticalScrollbarRef.current.style.top = `${scrollbarTop}px`;
        this.verticalScrollbarRef.current.style.height = `${scrollbarHeight}px`;
      }
    };

    render() {
      return (
        <WrappedComponent
          HorizontalScrollbar={
            <div
              className="Scrollbar Scrollbar--horizontal"
              id={this.horizontalScrollUuid}
              onMouseDown={this.horizontalScrollBarDragStart}
              ref={this.horizontalScrollbarRef}
            />
          }
          VerticalScrollbar={
            <div
              className="Scrollbar Scrollbar--vertical"
              id={this.verticalScrollUuid}
              onMouseDown={this.verticalScrollBarDragStart}
              ref={this.verticalScrollbarRef}
            />
          }
          setScrollableContainerRef={this.setupScrollInfo}
          {...this.props}
        />
      );
    }
  }

  return Wrapper;
};

export {withScrollbar};
